/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { groupBy, map, orderBy, isEmpty, find } from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';

import {
  groupingFunctionalMapping,
  mapAccountsToTableData
} from 'services/TableSupport';
import {
  getAccountPerformanceMap,
  getAccountsWithIntraday,
  getBalances,
  getMmrMessages
} from 'state/account/AccountSelectors';
import { getView } from 'state/view/ViewSelectors';
import { initialDashboardTableFilteringState } from 'state/account/SelectAccountsFilteringReducer';
import {
  getCustomerPlanningGroupId,
  isActiveHousehold
} from 'state/customer/CustomerSelectors';
import { isRasNonHouseholdAccount } from 'services/Accounts';
import { isActiveAdvisory } from 'services/Products';

export const getHistoricalBalances = (state) => state.historicalBalances;

export const getTransformAccountForHistoricalView = createSelector(
  getHistoricalBalances,
  (historicalBalances) => {
    const isHistoricalView =
      historicalBalances.selectedDate && historicalBalances.balances;
    return !isHistoricalView && isEmpty(historicalBalances.balances)
      ? (v) => v
      : ({ details, metaData }) => {
          const historicalAccount = find(historicalBalances.balances, {
            accountId: details.name.accountId
          });
          const marketValue =
            !historicalAccount || !historicalAccount.endingMarketValue
              ? 'n/a'
              : historicalAccount.endingMarketValue;

          return {
            children: [],
            details: { ...details, marketValue, balance: marketValue },
            metaData
          };
        };
  }
);

export const getBalancesCurrentDate = createSelector(
  getBalances,
  (balances) => {
    return moment.utc(balances.asOfDate);
  }
);

export const getAccountOverviewTableData = createSelector(
  getAccountsWithIntraday,
  (accounts) =>
    accounts
      .filter((account) => !account.purgedAccount)
      .filter((a) => !a.hiddenAccount)
);

export const getActiveHouseholdAccountOverviewTableData = createSelector(
  getAccountOverviewTableData,
  getCustomerPlanningGroupId,
  isActiveHousehold,
  (accounts, customerPlanningGroupId, isActiveHousehold) =>
    isActiveHousehold
      ? accounts.filter(
          (account) =>
            !(
              account.balanceValue === 0 &&
              isRasNonHouseholdAccount(account, customerPlanningGroupId)
            )
        )
      : accounts.filter(
          (account) =>
            !(
              isActiveAdvisory(account.productType) &&
              account.balanceValue === 0
            )
        )
);

export const getOrderedAccountOverviewTableData = createSelector(
  getActiveHouseholdAccountOverviewTableData,
  (accounts) => orderBy(accounts, groupingFunctionalMapping('default', true))
);

export const getAccountOverviewTableGroups = createSelector(
  getOrderedAccountOverviewTableData,
  (accounts) => {
    return groupBy(
      accounts,
      groupingFunctionalMapping('default', false, false)
    );
  }
);

export const getAccountOverviewTableMappedGroups = createSelector(
  getBalancesCurrentDate,
  getView,
  getAccountOverviewTableGroups,
  getAccountPerformanceMap,
  (currentDate, view, accountGroups, accountPerformanceMap) => {
    return map(accountGroups, (accountGroup) => {
      const filteringSelections = {
        ...initialDashboardTableFilteringState
      };
      return mapAccountsToTableData({
        filteringSelections,
        currentDate,
        view,
        transformAccount: (v) => v,
        isProductOrOwnership: true,
        accountPerformanceMap
      })(accountGroup);
    });
  }
);

export const getAccountOverviewTableDataWithMmr = createSelector(
  getAccountOverviewTableMappedGroups,
  getMmrMessages,
  (tableGroups, mmrMessages) => {
    if (!isEmpty(mmrMessages)) {
      const { messages } = mmrMessages;
      return tableGroups.map((group) => {
        const { children } = group;
        const updatedChildren = children.map((child) => {
          const {
            details: {
              name: { ticker }
            }
          } = child;
          return {
            ...child,
            mmrMessage: messages ? messages.get(ticker) : ''
          };
        });
        return {
          ...group,
          children: updatedChildren
        };
      });
    }
    return tableGroups;
  }
);
